/* eslint-disable @typescript-eslint/no-unsafe-call */
import { Button, Grid } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import * as ol from "ol";
import { Feature } from "ol";
import GeometryType from "ol/geom/GeometryType";
import Point from "ol/geom/Point";
import { Draw, Modify, Snap } from "ol/interaction";
import { transform } from "ol/proj";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Coordinate, DamDetailsDto } from "~/api/GeneratedApi";
import { CoordinateSelector } from "~/components/CoordinateSelector";
import GenericStepperDialog, {
  DialogStep,
} from "~/components/GenericStepperDialog";
import { addDamsToMap } from "~/pages/MapPage/functions/addDamsToMap";
import createDamLayer from "~/pages/MapPage/functions/createDamLayer";
import { findFeatureById } from "~/pages/MapPage/functions/findFeatureById";
import { findLayerByTitle } from "~/pages/MapPage/functions/findLayerByTitle";
import { findSourceByTitle } from "~/pages/MapPage/functions/findSourceByTitle";
import { mapToReadOnly } from "~/pages/MapPage/functions/mapToReadOnly";
import useCreateMap from "~/pages/MapPage/hooks/useCreateMap";
import { LayerTitle } from "~/pages/MapPage/types/layers";
import DamForm, { damDataToForm, DamFormInputs } from "./DamForm";

const useStyles = makeStyles((theme) => ({
  mapContainer: {
    height: "80%",
    width: "100%",
    marginBottom: theme.spacing(1),
  },
  drawActionButton: {
    display: "block",
    margin: "0 auto",
    height: 36,
    color: theme.palette.warning.contrastText,
    backgroundColor: theme.palette.warning.main,
    "&:hover": {
      backgroundColor: theme.palette.warning.dark,
    },
  },
}));

export type DamDialogAction = "add" | "edit" | "view";
type Props = {
  activeAction?: DamDialogAction;
  open: boolean;
  dam?: DamDetailsDto;
  onClose?: () => void;
  onAdd?: (d: DamFormInputs, coords: Coordinate) => void;
  onEdit?: (d: DamFormInputs, coords: Coordinate) => void;
};

const DamDialog = ({
  activeAction,
  dam,
  open,
  onClose,
  onAdd,
  onEdit,
}: Props): JSX.Element | null => {
  const classes = useStyles();
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<ol.Map>();
  const nextRef = useRef<() => void>();
  const drawRef = useRef<Draw>();
  const { t } = useTranslation("dam-page");
  const [coordinates, setCoordinates] = useState<Coordinate>();
  const [initiallyZoomed, setInitiallyZoomed] = useState(false);
  const [formData, setFormData] = useState<DamFormInputs>();
  const actions: DamDialogAction[] = ["add", "edit", "view"];
  const formId = useMemo(() => `${activeAction}-dam-form`, [activeAction]);
  const { createMap } = useCreateMap();

  const edit = useMemo(() => activeAction === "edit", [activeAction]);
  const add = useMemo(() => activeAction === "add", [activeAction]);
  const view = useMemo(() => activeAction === "view", [activeAction]);

  // Initial values
  useEffect(() => {
    if (!dam) return;
    const transformed = transform(
      [dam.location.x, dam.location.y],
      "EPSG:4326",
      "EPSG:3857",
    );
    setCoordinates({ x: transformed[0], y: transformed[1] });
    setFormData(damDataToForm(dam));
  }, [dam]);

  const handleClearDrawing = useCallback(() => {
    if (mapRef.current) {
      const source = findSourceByTitle(mapRef.current, LayerTitle.DamsDraw);
      source?.clear();
      setCoordinates(undefined);
    }
  }, []);

  const steps: DialogStep[] = useMemo(() => {
    let title = "";
    switch (activeAction) {
      case "add":
        title = t("fill-info");
        break;
      case "edit":
        title = t("edit-info");
        break;
      case "view":
        title = dam?.name ?? "";
        break;
      default:
        break;
    }
    const lastStep = {
      title: view ? title : t("general:confirm"),
      content: (
        <Grid container>
          <Grid
            item
            xs={4}
            ref={mapContainerRef}
            className={classes.mapContainer}
          />

          <Grid item xs={8}>
            <DamForm
              small
              onSubmit={() => null}
              formId={formId}
              formInitialValues={formData}
              readOnly
            />
          </Grid>
        </Grid>
      ),
    };
    if (view) return [lastStep];
    return [
      {
        title: t("select-location"),
        content: (
          <>
            <div ref={mapContainerRef} className={classes.mapContainer} />
            <Button
              variant="contained"
              classes={{ root: classes.drawActionButton }}
              onClick={handleClearDrawing}
            >
              {t("general:clear")}
            </Button>
            <CoordinateSelector
              coordinates={coordinates}
              onChange={setCoordinates}
            />
          </>
        ),
      },
      {
        title,
        content: (
          <DamForm
            onSubmit={(d) => {
              setFormData(d);
              nextRef.current?.();
            }}
            formId={formId}
            defaultValues={dam ?? undefined}
            readOnly={view}
          />
        ),
      },
      lastStep,
    ];
  }, [
    activeAction,
    t,
    classes.mapContainer,
    classes.drawActionButton,
    formId,
    formData,
    view,
    handleClearDrawing,
    coordinates,
    dam,
  ]);

  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const renderNextButton = useCallback(
    (step: number, next: () => void, close: () => void) => {
      if (view) return null;
      switch (step) {
        case 0:
          return (
            <Button
              onClick={() => next()}
              variant="contained"
              color="secondary"
              disabled={!coordinates}
            >
              {t("general:next")}
            </Button>
          );
        case 1:
          return (
            <Button
              type="submit"
              form={formId}
              variant="contained"
              color="secondary"
            >
              {t("general:next")}
            </Button>
          );
        case 2:
          return (
            <Button
              onClick={() => {
                if (!coordinates) return;
                // Coordinates are transformed from EPSG:3857 to EPSG:4326
                const txCoords = transform(
                  [coordinates.x, coordinates.y],
                  "EPSG:3857",
                  "EPSG:4326",
                );
                const transformed = { x: txCoords[0], y: txCoords[1] };
                if (add && formData && transformed) {
                  onAdd?.(formData, transformed);
                  close();
                } else if (edit && formData && transformed) {
                  onEdit?.(formData, transformed);
                  close();
                }
              }}
              variant="contained"
              color="secondary"
            >
              {t("general:save")}
            </Button>
          );

        default:
          return null;
      }
    },
    [view, coordinates, t, formId, add, formData, edit, onAdd, onEdit],
  );

  const renderBackButton = useCallback(
    (step, prev, handleDialogClose) => {
      if (view)
        return (
          <Button
            onClick={() => {
              handleDialogClose();
              handleClose();
            }}
          >
            {t("general:cancel")}
          </Button>
        );
      switch (step) {
        case 0:
          return (
            <Button
              onClick={() => {
                handleDialogClose();
                handleClose();
              }}
            >
              {t("general:cancel")}
            </Button>
          );
        case 1:
        case 2:
          return (
            <Button onClick={() => prev()}>{t("general:previous")}</Button>
          );

        default:
          return null;
      }
    },
    [handleClose, t, view],
  );

  const handleStepLogic = useCallback(
    (step: number) => {
      // Add map on step 0 and the required interactions
      if (step !== 0) {
        mapRef.current?.dispose();
        mapRef.current = undefined;
        drawRef.current = undefined;
      }

      if (view && mapContainerRef.current && dam?.location) {
        // Add the dam to map with needed layers etc
        mapRef.current = createMap({
          target: mapContainerRef.current,
          includedLayers: [LayerTitle.OSM, LayerTitle.Dams],
          popup: false,
          existingMap: mapRef.current,
        });
        addDamsToMap(mapRef.current, [dam], true);
        const damOnMap = findFeatureById(mapRef.current, `dam${dam.id}`);
        const ext = damOnMap?.getGeometry()?.getExtent();
        if (ext) {
          mapRef.current.getView().fit(ext);
          mapRef.current?.getView().setZoom(14);
        }
        mapToReadOnly(mapRef.current);
        return;
      }

      if (step === 0 && mapContainerRef.current) {
        if (!mapRef.current) {
          mapRef.current = createMap({
            target: mapContainerRef.current,
            includedLayers: [LayerTitle.OSM, LayerTitle.Dams],
            popup: false,
            existingMap: mapRef.current,
          });
          // Add a layer for drawing the dam
          const { layer: drawLayer, source } = createDamLayer(true);
          mapRef.current.addLayer(drawLayer);

          // Set the existing layer as visible, ignoring the actual settings
          findLayerByTitle(mapRef.current, LayerTitle.Dams)?.setVisible(true);
          drawRef.current = new Draw({
            source,
            type: GeometryType.POINT,
          });
          const snap = new Snap({ source });
          mapRef.current.addInteraction(snap);
          const modify = new Modify({ source });
          mapRef.current.addInteraction(modify);
          drawRef.current.on("drawend", (e) => {
            const c = (e.feature.getGeometry() as Point).getCoordinates();
            if (!c) return;
            setCoordinates({ x: c[0], y: c[1] });
          });
          modify.on("modifyend", (e) => {
            const c = (
              e.features.item(0)?.getGeometry() as Point
            ).getCoordinates();
            if (!c) return;
            setCoordinates({ x: c[0], y: c[1] });
          });
        }
        if (coordinates && drawRef.current && mapRef.current) {
          mapRef.current.removeInteraction(drawRef.current);
          const source = findSourceByTitle(mapRef.current, LayerTitle.DamsDraw);
          const point = new Point([coordinates.x, coordinates.y]);
          const existingFeat = findFeatureById(mapRef.current, "finished");

          if (!existingFeat) {
            const feat = new Feature({
              geometry: point,
            });
            feat.setId("finished");
            source.addFeature(feat);
          } else {
            existingFeat.setGeometry(point);
            source.clear();
            source.addFeature(existingFeat);
          }
          if (!initiallyZoomed && dam) {
            mapRef.current.getView().fit(point.getExtent());
            mapRef.current?.getView().setZoom(14);
            setInitiallyZoomed(true);
          }
        }
        if (!coordinates && drawRef.current) {
          mapRef.current.addInteraction(drawRef.current);
        }
      }
      if (step === 2 && mapContainerRef.current && !mapRef.current) {
        mapRef.current = createMap({
          target: mapContainerRef.current,
          includedLayers: [LayerTitle.OSM, LayerTitle.Dams],
          popup: false,
          existingMap: mapRef.current,
        });
        // Add a layer for the drawing
        const { layer: drawLayer, source } = createDamLayer(true);
        mapRef.current.addLayer(drawLayer);

        // Set the existing layer as visible, ignoring the actual settings
        findLayerByTitle(mapRef.current, LayerTitle.Dams)?.setVisible(true);
        if (coordinates) {
          const point = new Point([coordinates.x, coordinates.y]);
          const feat = new Feature({
            geometry: point,
          });
          feat.setId("finished");
          source.addFeature(feat);
          mapRef.current.getView().fit(point.getExtent());
          mapRef.current?.getView().setZoom(14);
        }
        mapToReadOnly(mapRef.current);
      }
    },
    [coordinates, createMap, dam, initiallyZoomed, view],
  );

  if (!activeAction || !actions.includes(activeAction)) return null;

  return (
    <GenericStepperDialog
      open={open}
      steps={steps}
      hideSteps={view}
      onClose={handleClose}
      dialogTitle=""
      minHeight="40%"
      height="95%"
      contentHeight="80%"
      renderNextButton={renderNextButton}
      renderBackButton={renderBackButton}
      onStepActive={handleStepLogic}
      nextRef={nextRef}
    />
  );
};

export default DamDialog;
